import type { ComponentLikeType } from 'elements/hardwareSelectorV2/types'
import { DISCOUNT_AMOUNT, EXCLUDED_DATA_KEYS } from 'hardware/constants'
import {
  DeliveryCostDataType,
  DiscountDataType,
  QuoteDataType,
  QuoteFilesLinkEntry,
  QuoteSummaryType,
  QuoteType,
} from 'hardware/types'
import type { BomLineItemType } from 'opensolar-common'
import type OrderLineItem from 'pages/ordering/OrderLineItem'
import { isMountingComponent } from 'util/misc'

/** Wire shape sent to the backend BOM serializer (`files_link[*]`). */
export type QuoteFilesLinkBackendEntry = {
  url: string
  document_type?: string
}

/** PrivateFile detail API URL — backend's BOM serializer resolves this from `files_link` to a PrivateFile record. */
export function privateFileDetailApiUrl(orgId: number, privateFileId: number): string {
  return `${window.API_ROOT}/api/orgs/${orgId}/private_files/${privateFileId}/`
}

/**
 * Normalize quote form `files_link` entries into the `{url, document_type?}` shape expected
 * by the backend BOM serializer. Drops entries without a usable URL.
 */
export function transformFilesLinkForBackend(
  filesLink: QuoteFilesLinkEntry[] | undefined | null
): QuoteFilesLinkBackendEntry[] {
  if (!Array.isArray(filesLink)) return []
  const result: QuoteFilesLinkBackendEntry[] = []
  for (const item of filesLink) {
    if (!item || typeof item.url !== 'string' || item.url.length === 0) continue
    const entry: QuoteFilesLinkBackendEntry = { url: item.url }
    if (typeof item.documentType === 'string' && item.documentType.length > 0) {
      entry.document_type = item.documentType
    }
    result.push(entry)
  }
  return result
}

export const DISTRIBUTOR_OUTLET = 'outlet'

/** Supplier part from ordering_v2 row (maps to quote `sku` and confirm-order distributorSku). */
export function supplierPartNumberFromOrdering(
  ordering: { variant_id?: string; distributor_sku?: string } | undefined,
  distributor?: string
): string {
  const variantId = ordering?.variant_id?.trim() || ''
  const distributorSku = ordering?.distributor_sku?.trim() || ''
  if (distributor === DISTRIBUTOR_OUTLET) {
    return distributorSku || variantId
  }
  return variantId || distributorSku
}

/** Part number for display / confirm-order from stored quote line. */
export function quoteLineSupplierPartNumber(
  item: Pick<QuoteDataType, 'sku' | 'variantId'>
): string | undefined {
  const partNo = item.sku?.trim() || item.variantId?.trim()
  return partNo || undefined
}

export const getComponentCategory = (componentType: string, componentTypeV2: string) => {
  if (!componentType) return null

  const categoryHashMap = {
    module: 'Panels',
    inverter: 'Inverter',
    battery: 'Battery',
    mounting: 'Mounting',
    other: 'Other',
  }
  const componentCategory = isMountingComponent(componentTypeV2) ? 'mounting' : componentTypeV2

  return categoryHashMap[componentCategory as keyof typeof categoryHashMap] ?? categoryHashMap['other']
}

/**
 * Resolves display category for a quote row, enriching stale `other`/`other` from catalog data.
 * When catalog is unavailable or the row already has a specific sub-type, falls through to `getComponentCategory`.
 */
export const getResolvedComponentCategory = (
  item: Pick<QuoteDataType, 'componentType' | 'componentTypeV2'>,
  catalog?: ComponentLikeType | null
) => {
  const resolvedTypeV2 =
    item.componentType === 'other' && item.componentTypeV2 === 'other' && catalog?.type
      ? catalog.type
      : item.componentTypeV2
  return getComponentCategory(item.componentType, resolvedTypeV2)
}

/**
 * BOM rows persisted on the project often carry generic `componentType` /
 * `componentTypeV2` (e.g. both `"other"`). Checkout line items carry resolved
 * catalog types. Prefer checkout when building the quote draft payload.
 */
export function mergeComponentTypesForQuotePayload(
  bomLineItem: Partial<Pick<BomLineItemType, 'componentType' | 'componentTypeV2'>>,
  checkoutLineItem: Partial<Pick<OrderLineItem, 'componentType' | 'componentTypeV2'>> | null | undefined
): { componentType: string; componentTypeV2: string } {
  return {
    componentType: checkoutLineItem?.componentType || bomLineItem.componentType || '',
    componentTypeV2:
      checkoutLineItem?.componentTypeV2 ||
      checkoutLineItem?.componentType ||
      bomLineItem.componentTypeV2 ||
      '',
  }
}

export const getQuoteSummary = (
  quote: QuoteType,
  deliveryCostData: DeliveryCostDataType | null | undefined,
  discountData: DiscountDataType | null | undefined,
  taxRate: number | null
) => {
  const quoteSummary: QuoteSummaryType = {
    itemCount: 0,
    subTotal: 0,
    discount: 0,
    deliveryCost: 0,
    taxAmount: 0,
    total: 0,
  }

  if (quote?.data) {
    let itemCount = 0
    let subTotal = 0

    const deliveryCost = parseFloat((deliveryCostData?.cost || 0).toFixed(2))
    Object.keys(quote?.data as Object)
      .filter((key) => !EXCLUDED_DATA_KEYS.includes(key))
      .forEach((key) => {
        const item = quote?.data[key] as QuoteDataType
        if (item) {
          itemCount = itemCount + item.quantity
          subTotal = subTotal + item.price * item.quantity
        }
      })

    subTotal = parseFloat(subTotal.toFixed(2))
    let discount = 0
    if (discountData) {
      discount = parseFloat(
        (discountData?.type === 'fixed'
          ? discountData?.amount
          : (subTotal * (discountData?.percentage || 0)) / 100
        ).toFixed(2)
      )
    }

    const total = subTotal - discount + deliveryCost
    const taxAmount = parseFloat((total * (taxRate ?? 0)).toFixed(2))

    const quoteSummaryItems = {
      itemCount,
      subTotal,
      discount,
      deliveryCost,
      taxAmount,
      total: total + taxAmount,
    }

    Object.keys(quoteSummaryItems).forEach((key) => {
      quoteSummary[key as keyof QuoteSummaryType] = quoteSummaryItems[key as keyof QuoteSummaryType]
    })
  }

  return quoteSummary
}

export const getUpdatedDiscountData = (quote: QuoteType) => {
  const discountData: DiscountDataType = quote?.data?.[DISCOUNT_AMOUNT] as DiscountDataType
  const quoteSummary = getQuoteSummary(quote, null, discountData, null)
  return {
    ...discountData,
    amount: quoteSummary.discount,
  }
}
